/**
 * The Logs screen's arithmetic. No React, no fetch, no clock.
 *
 * Two rules live here because both are easy to get subtly wrong inline and
 * impossible to see once they are wrong: what the pager's "showing x-y of z"
 * actually is, and whether any filter is on at all - which is the difference
 * between "nothing has happened yet" and "your filter matched nothing".
 */
import type { CrmLogQuery } from "../../../../lib/crmLogs";

/** The value every dropdown uses for "no choice". Never a real filter value. */
export const ANY = "__any__";

export interface LogFilters {
  q: string;
  module: string;
  action: string;
  actorUserId: string;
  from: string;
  to: string;
}

export const EMPTY_FILTERS: LogFilters = {
  q: "",
  module: ANY,
  action: ANY,
  actorUserId: ANY,
  from: "",
  to: "",
};

/**
 * Whether the operator has narrowed anything.
 *
 * Drives the empty card's copy, and that is the whole reason it exists. "No
 * activity has been recorded yet" shown under six active filters sends somebody
 * looking for a bug in the ledger rather than at the filter row.
 */
export function isFiltered(f: LogFilters): boolean {
  return (
    f.q.trim() !== "" ||
    f.module !== ANY ||
    f.action !== ANY ||
    f.actorUserId !== ANY ||
    f.from !== "" ||
    f.to !== ""
  );
}

/**
 * The filters as the API wants them: chosen values only, `ANY` dropped.
 *
 * An exact action wins over its module on the server as well - both being set
 * is the ordinary UI state (a module picked, then one of its actions), and the
 * narrower one is what the operator meant.
 */
export function toQuery(f: LogFilters): CrmLogQuery {
  return {
    ...(f.q.trim() ? { q: f.q.trim() } : {}),
    ...(f.module !== ANY ? { module: f.module } : {}),
    ...(f.action !== ANY ? { action: f.action } : {}),
    ...(f.actorUserId !== ANY ? { actorUserId: f.actorUserId } : {}),
    ...(f.from ? { from: f.from } : {}),
    ...(f.to ? { to: f.to } : {}),
  };
}

/**
 * The pager's window, one-based and inclusive.
 *
 * `rowCount` is the length of the page actually returned, not the page size:
 * the last page is short, and computing `to` from the page size claims rows
 * that are not there.
 */
export function showingRange(page: number, pageSize: number, rowCount: number): { from: number; to: number } {
  if (rowCount <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  return { from, to: from + rowCount - 1 };
}

/** How many pages a total spans. Always at least one, so the pager has a floor. */
export function pageCountOf(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * One end of the range as a `Date`, or nothing when that end is empty.
 *
 * The filter row keeps plain `YYYY-MM-DD` strings because that is what the API
 * takes, while the shared picker bounds its calendar with `Date` objects. The
 * hour matters: the calendar hands its `disabled` test a midnight-local day and
 * compares it with `<`, so a bound built at any other hour would rule out the
 * boundary day itself - "to 31 August" would refuse 31 August.
 */
export function boundDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * A module's name in words, falling back to the stored key.
 *
 * The same rule `auditActionLabel` follows and for the same reason: modules
 * appear in this list because a route wrote one, not because anybody catalogued
 * it, so an untranslated module must still read as a word rather than as a
 * missing translation key.
 */
export function moduleLabel(key: string, t: (k: string) => string): string {
  const path = `settings.logs.module.${key}`;
  const label = t(path);
  return label === path ? key : label;
}
